// Rôles utilisateur ELECTRA - source de vérité symétrique à backend/utils/roles.py
// Ne pas dupliquer ces listes ailleurs : importer depuis ce module.

// ──────────────────────────────────────────────────────────────────────
// Briques de base par industrie
// ──────────────────────────────────────────────────────────────────────

// Rôles "ops" par industrie (surveillance continue / pilotage temps réel)
export const OPS_ROLES = ['production', 'dc_exploitation', 'building_manager'];

// Rôles "facility" par industrie (maintenance / qualité réseau)
export const FACILITY_ROLES = ['maintenance', 'dc_facility', 'building_technician'];

// Tous les rôles opérationnels (manufacturing + datacenter + bâtiment) + chef_production
// Symétrique à backend/utils/roles.py::OPERATIONAL_ROLES
export const OPERATIONAL_ROLES = [
  'production',
  'chef_production',
  'maintenance',
  'dc_exploitation',
  'dc_facility',
  'building_manager',
  'building_technician',
];

// Rôles transverses (multi-industrie, vue agrégée)
// Symétrique à backend/utils/roles.py::TRANSVERSE_ROLES
// - energy_manager : responsable énergie d'un site (coûts, énergie par ligne,
//   kWh/tonne, dérives). Lecture seule, mono-site, pas d'actions admin.
// - affichage : compte écran TV d'usine, lecture seule stricte. Il ne voit
//   JAMAIS la sidebar : il n'apparaît dans AUCUNE liste de menu ci-dessous
//   et atterrit directement sur /dashboard/tv.
export const TRANSVERSE_ROLES = ['direction', 'direction_centrale', 'admin_client', 'analyste', 'energy_manager', 'affichage'];

// ──────────────────────────────────────────────────────────────────────
// Compositions usuelles
// ──────────────────────────────────────────────────────────────────────

// Direction (site + multi-site)
export const DIRECTION_ROLES = ['direction', 'direction_centrale'];

// Tous les rôles "métier" : ops + facility (toutes industries) + direction + chef_production
// chef_production a accès à tout ce qu'un opérateur production voit (zones, alerts,
// breakers, etc.) car il supervise les opérateurs. Il a EN PLUS le dashboard
// Validations Tonnage via TONNAGE_MENU_ROLES.
// energy_manager inclus : lecture des vues opérationnelles transverses
// (zones, comparison, breakers, alerts) pour le suivi énergie du site.
// Utilisé pour les routes accessibles à n'importe quel utilisateur opérationnel.
export const ALL_OPERATIONAL = [...OPS_ROLES, ...FACILITY_ROLES, ...DIRECTION_ROLES, 'chef_production', 'energy_manager'];

// ALL_OPERATIONAL + super_admin (pour vues détaillées multi-industry : facility/PUE, kWh/m²)
export const ALL_OPERATIONAL_WITH_ADMIN = [...ALL_OPERATIONAL, 'super_admin'];

// Rôles plateforme ELECTRA (cabinet)
export const PLATFORM_ADMIN_ROLES = ['super_admin', 'admin'];

// Rôles avec privilèges admin license (peuvent gérer utilisateurs/réglages site)
export const ADMIN_LICENSE_ROLES = ['direction', 'direction_centrale', 'admin_client', 'admin', 'super_admin'];

// Rôles avec accès au workflow tonnage
// (chef_production valide, direction supervise, admin_client peut piloter, super_admin peut tout)
export const TONNAGE_AUTHORIZATION_ROLES = ['chef_production', 'direction', 'direction_centrale', 'admin_client', 'super_admin'];

// ──────────────────────────────────────────────────────────────────────
// Routes spécifiques (dashboards par rôle dédié datacenter / office)
// Pattern commun : [rôle_principal, rôle_pair_industrie, direction, direction_centrale, admin_client, super_admin]
// ──────────────────────────────────────────────────────────────────────

// Accès aux dashboards "couple datacenter" (dc-exploitation / dc-facility)
export const DATACENTER_DASHBOARD_ROLES = ['dc_exploitation', 'dc_facility', 'direction', 'direction_centrale', 'admin_client', 'super_admin'];

// Accès aux dashboards "couple bâtiment" (building-manager / building-technician)
export const BUILDING_DASHBOARD_ROLES = ['building_manager', 'building_technician', 'direction', 'direction_centrale', 'admin_client', 'super_admin'];

// Rôles autorisés à exporter (super_admin + admin_client + direction + tous opérationnels industries + chef_production + energy_manager)
export const EXPORT_ROLES = ['super_admin', 'admin_client', 'direction_centrale', 'direction', ...OPS_ROLES, ...FACILITY_ROLES, 'chef_production', 'energy_manager'];

// ──────────────────────────────────────────────────────────────────────
// Spécifiques manufacturing (production / chef_production)
// ──────────────────────────────────────────────────────────────────────

// Production dashboard (production + chef + direction + energy_manager)
// energy_manager : lecture de l'énergie par ligne / kWh de production.
export const PRODUCTION_DASHBOARD_ROLES = ['production', 'chef_production', ...DIRECTION_ROLES, 'energy_manager'];

// Production metrics (production + chef_production + direction + energy_manager)
// Le chef voit aussi les metriques production pour supervision globale.
// energy_manager : suivi du kWh/tonne et des dérives d'intensité énergétique.
export const PRODUCTION_METRICS_ROLES = ['production', 'chef_production', ...DIRECTION_ROLES, 'energy_manager'];

// Maintenance dashboard
export const MAINTENANCE_DASHBOARD_ROLES = ['maintenance', ...DIRECTION_ROLES];

// ──────────────────────────────────────────────────────────────────────
// Visibilité sidebar (Layout.jsx) — variantes "métier seulement"
// Les admin_client / super_admin ont accès aux routes (cf. *_DASHBOARD_ROLES ci-dessus)
// mais ne voient pas ces items dans la sidebar : ils naviguent via leur interface dédiée.
// ──────────────────────────────────────────────────────────────────────

// Sidebar : item Direction (dashboard coûts / KPIs business)
// energy_manager y accède en LECTURE pour les coûts d'énergie du site.
// Ne PAS réutiliser DIRECTION_ROLES ici : cette liste sert ailleurs à des
// privilèges direction (multi-site, tonnage, menus datacenter/bâtiment…)
// que energy_manager ne doit pas hériter.
export const DIRECTION_MENU_ROLES = [...DIRECTION_ROLES, 'energy_manager'];

// Sidebar : items couple datacenter (dc-exploitation / dc-facility)
export const DATACENTER_MENU_ROLES = ['dc_exploitation', 'dc_facility', ...DIRECTION_ROLES];

// Sidebar : items couple bâtiment (building-manager / building-technician)
export const BUILDING_MENU_ROLES = ['building_manager', 'building_technician', ...DIRECTION_ROLES];

// Sidebar : item Validations Tonnage (chef_production)
// Note: pas de super_admin ici (il a sa propre UI admin), tandis que la route App.js l'autorise.
export const TONNAGE_MENU_ROLES = ['chef_production', ...DIRECTION_ROLES, 'admin_client'];

// Sidebar : Zones (manufacturing uniquement — datacenter/bâtiment ont leurs propres vues)
// chef_production inclus : le chef visualise les zones comme les operateurs production.
// energy_manager inclus : lecture de l'énergie par zone/atelier.
export const ZONES_MENU_ROLES = ['production', 'chef_production', 'maintenance', ...DIRECTION_ROLES, 'energy_manager'];
